import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { EditResearch } from "../../../../services/EditarPesquisa";
import { LoginContext } from "../../../../contexts/login_context";
import PreviewEmbedModal from "../../PreviewEmbedModal";
import * as xlsx from "xlsx";

type ModalEditProps = {
  closeModal?: () => void;
  selectRow?: any;
  onSuccess?: () => void;
};

export type EditResearchPayload = {
  inicio: string;
  termino: string;
  inativo: boolean;
  titulo?: string;
  embedCode?: string;
  cadsSelecionados?: string[];
};

export default function ModalEdit({ closeModal, selectRow, onSuccess }: ModalEditProps) {
  const [formData, setFormData] = useState({
    publico: "Enviar planilha",
    file: null as File | null,
    textareaGTINs: "",
    embedCode: "",
    researchPeriod: {
      start: "",
      end: "",
    },
    status: true,
    titulo: "",
    openEmbedCode: false,
    showTable: false,
  });

  const [openEmbedCode, setOpenEmbedCode] = useState(false);
  const { jwt } = useContext(LoginContext);

  const [v, setV] = useState("");     
  const [cadPreview, setCadPreview] = useState<string[]>([]);
  const [cadCount, setCadCount] = useState(0);

  const handleChange = (
    name: string,
    value:
      | string
      | boolean
      | File
      | null
      | {
          start: string;
          end: string;
        }
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/;

  const toDateInput = (s?: string) => {
    if (!s) return "";
    if (yyyymmdd.test(s)) return s;
    if (ddmmyyyy.test(s)) return s.replace(ddmmyyyy, "$3-$2-$1");

    try {
      const isoDate = new Date(s);
      if (!isNaN(isoDate.getTime())) {
        return isoDate.toISOString().split("T")[0];
      }
    } catch (e) {}

    return "";
  };

  function normalizeCadList(input: string) {
    const replaced = input.replace(/[\r\n;]+/g, ",");

    const parts = replaced
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (parts.length === 0) {
      return {
        ok: false,
        error: "Nenhum CAD encontrado. Verifique o conteúdo.",
      };
    }

    const invalid = parts.filter((p) => !/^[A-Za-z0-9]+$/.test(p));
    if (invalid.length > 0) {
      return {
        ok: false,
        error: `Foram encontrados CADs inválidos: ${invalid
          .slice(0, 5)
          .join(", ")}`,
      };
    }

    return {
      ok: true,
      value: parts.join(","),
      count: parts.length,
    };
  }

    async function handleGetFile(file: File) {
      try {
        const ext = String(file.name).split(".").pop()?.toLowerCase();

        if (!ext || !["txt", "csv", "xlsx"].includes(ext)) {
          toast.error("Tipo de arquivo inválido. Envie apenas .xlsx, .txt ou .csv");
          return;
        }

        let raw = "";

        if (ext === "txt" || ext === "csv") {
          raw = await file.text();
        }

        if (ext === "xlsx") {
          const reader = new FileReader();

          const buffer: ArrayBuffer = await new Promise((resolve, reject) => {
            reader.onload = (e) => {
              if (!e.target?.result) {
                reject(new Error("Falha ao ler arquivo."));
              } else {
                resolve(e.target.result as ArrayBuffer);
              }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
          });

          const workbook = xlsx.read(buffer, { type: "buffer" });
          const workSheetName = workbook.SheetNames[0];
          const workSheet = workbook.Sheets[workSheetName];

          const lines: string[] = xlsx
            .utils.sheet_to_csv(workSheet)
            .split("\n")
            .filter((element: string | any[] | null) => {
              if (element != null && element.length > 0) return true;
            });

          raw = lines.join("\n");
        }

        const { ok, value, count, error } = normalizeCadList(raw);

        if (!ok || !value) {
          toast.error(error || "Arquivo de CADs inválido.");
          setV("");
          setCadPreview([]);
          setCadCount(0);
          return;
        }

        const parts = value.split(",");

        setV(value);
        setCadPreview(parts);
        setCadCount(count);

        setFormData((prev) => ({
          ...prev,
          publico: "Digitar CADs",
          textareaGTINs: parts.join(", "),
          file, 
        }));


      toast.success(`Arquivo carregado com ${count} CAD(s).`);
    } catch (error) {
      console.log(error);
      toast.error("Erro ao ler arquivo de CADs.");
    }
  }


  useEffect(() => {
    if (selectRow) {
      const cadsList: string[] = (selectRow.cadsSelecionados || []).map(
        (cad: string) => cad.replace(/"/g, "").trim()
      );

      const temCads = Array.isArray(cadsList) && cadsList.length > 0;

      setFormData((prev) => ({
        ...prev,
        titulo: selectRow.titulo || "",
        textareaGTINs: cadsList.join(", "),
        embedCode: selectRow.embedCode || "",
        researchPeriod: {
          start: toDateInput(selectRow.inicio),
          end: toDateInput(selectRow.termino),
        },
        status: !selectRow.inativo,
        publico: temCads ? "Digitar CADs" : prev.publico,
      }));

      setCadPreview(cadsList);
      setCadCount(cadsList.length);
      setV(cadsList.join(","));
    }
  }, [selectRow]);


  const handleSaveChanges = async () => {
    try {
      const inicio = formData.researchPeriod.start?.trim();
      const termino = formData.researchPeriod.end?.trim();

      if (!inicio || !termino) {
        toast.error("Preencha o período de divulgação (início e fim).");
        return;
      }
      if (termino <= inicio) {
        toast.error("A data fim não pode ser igual ou menor que a data de início.");
        return;
      }

      let cadsFinal: string[] = [];

      if (formData.publico === "Digitar CADs") {
        if (!formData.textareaGTINs.trim()) {
          toast.error("Digite pelo menos um CAD.");
          return;
        }

        const { ok, value, error } = normalizeCadList(formData.textareaGTINs);
        if (!ok || !value) {
          toast.error(error || "Lista de CADs digitada é inválida.");
          return;
        }
        cadsFinal = value.split(",");
      } else if (formData.publico === "Enviar planilha") {
        if (!v.trim()) {
          toast.error("Você precisa carregar um arquivo válido de CADs.");
          return;
        }

        const { ok, value, error } = normalizeCadList(v);
        if (!ok || !value) {
          toast.error(error || "Arquivo de CADs é inválido.");
          return;
        }
        cadsFinal = value.split(",");
      }

      const payload: EditResearchPayload = {
        inicio,
        termino,
        inativo: !formData.status,
        titulo: formData.titulo || "",
        embedCode: formData.embedCode || "",
        cadsSelecionados: cadsFinal,
      };

      const editRes = await EditResearch(jwt, selectRow._id, payload);

      if (editRes.success) {
        toast.success("Pesquisa editada com sucesso!");

        onSuccess && onSuccess();

        closeModal && closeModal();
      } else {
        toast.error("Erro ao editar pesquisa: " + editRes.message);
      }
    } catch (error) {
      toast.error("Erro ao editar pesquisa.");
    }
  };


  return (
    <div className="containerNewResearch">
      <div className="containerContentNewResearch">
        <div className="form-group">
          <span className="form-label">Título</span>
          <input
            type="text"
            className="form-control"
            placeholder="Digite aqui"
            value={formData.titulo}
            onChange={(e) => handleChange("titulo", e.target.value)}
          />
        </div>

        <div className="form-group">
          <span className="form-label">Público alvo da pesquisa</span>
          <div className="form-group-radio">
            <div>
              <label>
                <input
                  type="radio"
                  name="publico"
                  value="Enviar planilha"
                  checked={formData.publico === "Enviar planilha"}
                  onChange={(e) => handleChange("publico", e.target.value)}
                />{" "}
                Enviar planilha
              </label>
            </div>

            <div>
              <label>
                <input
                  type="radio"
                  style={{ marginLeft: "15px" }}
                  name="publico"
                  value="Digitar CADs"
                  checked={formData.publico === "Digitar CADs"}
                  onChange={(e) => handleChange("publico", e.target.value)}
                />{" "}
                Digitar CADs
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <span className="form-label">
            {formData.publico === "Enviar planilha"
              ? "Enviar uma lista com números de cad, organizados em uma única coluna"
              : "Digite CADs separados por vírgula"}
          </span>

          {formData.publico === "Enviar planilha" ? (
            <>
              <label
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  width: "100%",
                  height: "150px",
                  border: "2px dashed #1f3567",
                  borderRadius: "3px",
                  cursor: "pointer",
                  background: "#e5effc",
                  color: "#555",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                <span>Clique ou arraste um arquivo aqui</span>
                <small style={{ color: "#888", marginTop: "5px" }}>
                  Tipos aceitos: .xlsx, .txt, .csv
                </small>
                <input
                  id="file-upload"
                  type="file"
                  accept=".xlsx, .txt, .csv"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      handleGetFile(f);
                      e.target.value = "";
                    }
                  }}
                />
              </label>

              {cadCount > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "8px 10px",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                    fontSize: "13px",
                    color: "#333",
                  }}
                >
                  <strong>{cadCount} CAD(s) carregados.</strong>
                  <div style={{ marginTop: "4px" }}>
                    Pré-visualização:&nbsp;
                    <code style={{ color: "#354674" }}>
                      {cadPreview.slice(0, 20).join(", ")}
                      {cadPreview.length > 20 && " ..."}
                    </code>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <textarea
                rows={4}
                className="form-control"
                placeholder="Digite aqui"
                value={formData.textareaGTINs}
                onChange={(e) => handleChange("textareaGTINs", e.target.value)}
              />

              {cadCount > 0 && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "8px 10px",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                    fontSize: "13px",
                    color: "#333",
                  }}
                >
                  <strong>{cadCount} CAD(s) atuais.</strong>
                  <div style={{ marginTop: "4px" }}>
                    Pré-visualização:&nbsp;
                    <code style={{ color: "#354674" }}>
                      {cadPreview.slice(0, 20).join(", ")}
                      {cadPreview.length > 20 && " ..."}
                    </code>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-group">
          <span className="form-label">Código de pesquisa</span>
          <textarea
            rows={4}
            className="form-control"
            placeholder="Embed Code"
            value={formData.embedCode}
            onChange={(e) => handleChange("embedCode", e.target.value)}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-primary btn-sm"
            style={{ marginBottom: "20px" }}
            onClick={() => {
              handleChange("openEmbedCode", true);
              setOpenEmbedCode(true);
            }}
          >
            Mostrar embed code
          </button>
        </div>

        <div className="form-group">
          <span className="form-label">Periodo de divulgação da pesquisa</span>
          <div className="form-group-date">
            <div>
              <input
                type="date"
                className="form-control"
                value={formData.researchPeriod.start}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    researchPeriod: {
                      ...prev.researchPeriod,
                      start: e.target.value,
                    },
                  }))
                }
                placeholder="inicio (dd/MM/yyyy)"
              />
            </div>
            <div>
              <input
                type="date"
                className="form-control"
                style={{ marginLeft: "15px" }}
                value={formData.researchPeriod.end}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    researchPeriod: {
                      ...prev.researchPeriod,
                      end: e.target.value,
                    },
                  }))
                }
                placeholder="Fim (dd/MM/yyyy)"
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <span className="form-label">Status</span>
          <label style={{ marginLeft: "15px", cursor: "pointer" }}>
            <input
              type="checkbox"
              style={{ display: "none" }}
              checked={formData.status}
              onChange={(e) => handleChange("status", e.target.checked)}
            />
            <span
              style={{
                display: "inline-block",
                width: "40px",
                height: "20px",
                background: formData.status ? "#4caf50" : "#ccc",
                borderRadius: "20px",
                position: "relative",
                transition: "0.3s",
              }}
            >
              <span
                style={{
                  content: "",
                  position: "absolute",
                  top: "2px",
                  left: formData.status ? "22px" : "2px",
                  width: "16px",
                  height: "16px",
                  background: "#fff",
                  borderRadius: "50%",
                  transition: "0.3s",
                }}
              />
            </span>
          </label>
        </div>
      </div>

      <div className="containerButtonsNewResearch">
        <div>
          <button className="btn btn-outline-primary btn-sm" onClick={closeModal}>
            Cancelar
          </button>
        </div>
        <div>
          <button
            className="btn btn-primary btn-sm"
            style={{ marginLeft: "15px" }}
            onClick={handleSaveChanges}
          >
            Salvar
          </button>
        </div>
      </div>

      <div>
        <PreviewEmbedModal
          stateModal={openEmbedCode}
          close={() => {
            setOpenEmbedCode(!openEmbedCode);
          }}
          embedCode={formData.embedCode}
        />
      </div>
    </div>
  );
}
